/**
 * organizations — Phase 1 of the org-information build (2026-08-15 owner
 * directive: organization information and personal information are different
 * things, stored and permissioned differently).
 *
 * Model:
 *   organizations/{orgId}                 org facts (identity + org contact)
 *   organizations/{orgId}/members/{uid}   membership: role + permission set
 *   users/{uid}.organizations             back-pointer array of orgIds
 *   users/{uid}.primary_org               the org driving workspace defaults
 *
 * Design decisions:
 *   · SELF-FOUNDED ORG ID IS DETERMINISTIC: `org_{uid}`. ensureOrganization
 *     can be called from any surface, twice, in a race — a deterministic id
 *     makes the mint idempotent with no transaction gymnastics.
 *   · users/{uid} root org fields (firm_name / organization_employee_count)
 *     become a PROJECTION of the org doc: saving the org mirrors them back so
 *     the plan recommender and older surfaces keep working untouched.
 *   · Org contact (main_phone / main_email / address) is ORGANIZATION-level
 *     contact — never a member's personal phone/email. Member personal data
 *     stays under users/{uid} and is not readable by co-members (rules).
 */

import { getAuth } from 'firebase/auth';
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { bandForTeamSizeLabel } from './profileWriteThrough';

const fns = () => getFunctions(getApp());

export type OrgRole = 'owner' | 'admin' | 'member' | 'billing' | 'viewer';

export interface OrgPermissions {
  edit_org_info: boolean;
  manage_billing: boolean;
  invite: boolean;
}

export interface Organization {
  id: string;
  name: string;
  size_band: string;
  industry: string;
  website: string;
  focus: string;
  notes: string;
  main_phone: string;
  main_email: string;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  owner_uid: string;
  created_from?: string;
}

export interface OrgMembership {
  role: OrgRole;
  permissions: OrgPermissions;
}

/** Default permission template per role — Phase 2 lets admins override per member. */
export function defaultPermissionsForRole(role: OrgRole): OrgPermissions {
  switch (role) {
    case 'owner':
    case 'admin':
      return { edit_org_info: true, manage_billing: true, invite: true };
    case 'billing':
      return { edit_org_info: false, manage_billing: true, invite: false };
    case 'member':
      return { edit_org_info: false, manage_billing: false, invite: true };
    case 'viewer':
    default:
      return { edit_org_info: false, manage_billing: false, invite: false };
  }
}

/** Deterministic id for the org a user founds themselves (idempotent mint). */
export function selfFoundedOrgId(uid: string): string {
  return `org_${uid}`;
}

/**
 * Facts doc for a WORKSPACE (multi-org D1 bridge, 2026-08-16): every
 * workspace accountId is its founder's uid, so the self-founded scheme
 * already IS the per-workspace scheme — org facts for workspace X live at
 * organizations/org_X, no migration.
 */
export function orgIdForWorkspace(accountId: string): string {
  return `org_${accountId}`;
}

export interface EnsureOrgFactsResult {
  orgId: string;
  accountId: string;
  ownerUid: string;
  native: boolean;
  created: boolean;
  role: string;
}

/**
 * Server-side ensure of the facts doc + the caller's membership for a
 * workspace they verifiably belong to — the admitted path for invited
 * members, whom the rules bar from self-registering into someone else's
 * org. Null on failure (surfaces render what they can).
 */
export async function ensureOrgFactsForWorkspace(
  accountId: string,
): Promise<EnsureOrgFactsResult | null> {
  try {
    const fn = httpsCallable<{ accountId: string }, EnsureOrgFactsResult>(fns(), 'ensureOrgFacts');
    const res = await fn({ accountId });
    return res.data;
  } catch (e) {
    console.warn('[organizations] ensureOrgFacts failed', e);
    return null;
  }
}

export type RenameOutcome =
  | { status: 'renamed'; policy: 'light' | 'ceremony' }
  | { status: 'unchanged' }
  | { status: 'code-sent'; maskedEmail: string }
  | { status: 'error'; reason: string; message: string };

/**
 * Governed rename (§6A): all name changes go through the server. Returns
 * 'code-sent' when the ceremony path mailed a 2FA code — call again with the
 * code to finish. Errors come back as data (reason: 'reauth-required' |
 * 'cooldown' | 'invalid-name' | 'bad-code' | 'expired' | …) so pages can
 * render the right guidance instead of a raw exception.
 */
export async function renameWorkspaceOrganization(
  accountId: string,
  name: string,
  code?: string,
): Promise<RenameOutcome> {
  try {
    const fn = httpsCallable<
      { accountId: string; name: string; code?: string },
      { status: 'renamed' | 'unchanged' | 'code-sent'; policy?: 'light' | 'ceremony'; maskedEmail?: string }
    >(fns(), 'renameOrganization');
    const res = await fn(code ? { accountId, name, code } : { accountId, name });
    if (res.data.status === 'renamed') return { status: 'renamed', policy: res.data.policy || 'light' };
    if (res.data.status === 'code-sent') return { status: 'code-sent', maskedEmail: res.data.maskedEmail || '' };
    return { status: 'unchanged' };
  } catch (e: any) {
    const reason = typeof e?.details?.code === 'string' ? e.details.code : 'unknown';
    const message = typeof e?.message === 'string' ? e.message : 'rename failed';
    return { status: 'error', reason, message };
  }
}

/** Editable org fields — the shape both the page form and save path use. */
export const ORG_EDITABLE_FIELDS = [
  'name', 'size_band', 'industry', 'website', 'focus', 'notes',
  'main_phone', 'main_email',
  'street_address', 'city', 'state_province', 'postal_code', 'country',
] as const;

function orgFromDoc(id: string, d: any): Organization {
  const s = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    id,
    name: s(d?.name),
    size_band: s(d?.size_band),
    industry: s(d?.industry),
    website: s(d?.website),
    focus: s(d?.focus),
    notes: s(d?.notes),
    main_phone: s(d?.main_phone),
    main_email: s(d?.main_email),
    street_address: s(d?.street_address),
    city: s(d?.city),
    state_province: s(d?.state_province),
    postal_code: s(d?.postal_code),
    country: s(d?.country),
    owner_uid: s(d?.owner_uid),
    created_from: s(d?.created_from) || undefined,
  };
}

/**
 * Lazy migration + founder mint. Ensures the signed-in user has at least one
 * organization: if none, mints `org_{uid}` seeded from everything we already
 * know (users/{uid} root org projection, onboarding profile, redemption
 * persona) and registers the caller as owner. Returns the primary orgId, or
 * null when signed out / on failure (callers render an empty state).
 */
export async function ensureOrganization(seed?: {
  name?: string;
  teamSizeLabel?: string;
  industry?: string;
  createdFrom?: string;
}): Promise<string | null> {
  try {
    const user = getAuth().currentUser;
    if (!user) return null;
    const db = getFirestore();
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    const userData: any = userSnap.exists() ? userSnap.data() : null;

    const existing: string[] = Array.isArray(userData?.organizations) ? userData.organizations : [];
    if (existing.length > 0) {
      return typeof userData?.primary_org === 'string' && userData.primary_org
        ? userData.primary_org
        : existing[0];
    }

    // Mint — gather every fact already on file (all optional, merge-if-present).
    let onboarding: any = null;
    try {
      const snap = await getDoc(doc(db, 'accounts', user.uid, 'onboarding', 'profile'));
      onboarding = snap.exists() ? snap.data() : null;
    } catch { /* optional source */ }
    let persona: any = null;
    try {
      const snap = await getDoc(doc(db, 'accounts', user.uid, 'profile', 'main'));
      persona = snap.exists() ? snap.data() : null;
    } catch { /* optional source */ }

    const name =
      seed?.name || onboarding?.firmName || userData?.firm_name || persona?.firmName || '';
    const band =
      bandForTeamSizeLabel(seed?.teamSizeLabel) ||
      (typeof userData?.organization_employee_count === 'string'
        ? userData.organization_employee_count
        : bandForTeamSizeLabel(onboarding?.firmSize)) || '';

    const orgId = selfFoundedOrgId(user.uid);
    const orgRef = doc(db, 'organizations', orgId);
    // Idempotent create — but if the doc already exists (the server-side
    // workspace bridge can win the race), leave its facts alone: a client
    // update that changed `name` would be denied by the §6A rename freeze.
    const orgSnap = await getDoc(orgRef);
    if (!orgSnap.exists()) {
      await setDoc(orgRef, {
        name,
        size_band: band,
        industry: seed?.industry || persona?.industry || '',
        focus: persona?.focus || '',
        website: persona?.website || '',
        owner_uid: user.uid,
        created_from: seed?.createdFrom || 'lazy-migration',
        created_at: serverTimestamp(),
      }, { merge: true });
    }
    await setDoc(doc(db, 'organizations', orgId, 'members', user.uid), {
      role: 'owner',
      permissions: defaultPermissionsForRole('owner'),
      // Display name is how co-workers know each other — org information.
      // Personal phone/email stay under users/{uid}, never on member docs.
      display_name: user.displayName || '',
      joined_at: serverTimestamp(),
    }, { merge: true });
    await setDoc(userRef, {
      organizations: arrayUnion(orgId),
      primary_org: orgId,
    }, { merge: true });
    return orgId;
  } catch (e) {
    console.warn('[organizations] ensureOrganization failed', e);
    return null;
  }
}

/** Load one organization (null when unreadable/missing). */
export async function loadOrganization(orgId: string): Promise<Organization | null> {
  try {
    const snap = await getDoc(doc(getFirestore(), 'organizations', orgId));
    return snap.exists() ? orgFromDoc(orgId, snap.data()) : null;
  } catch (e) {
    console.warn('[organizations] load failed', e);
    return null;
  }
}

/** The caller's membership in an org (null → treat as no access). */
export async function loadMyMembership(orgId: string): Promise<OrgMembership | null> {
  try {
    const user = getAuth().currentUser;
    if (!user) return null;
    const snap = await getDoc(doc(getFirestore(), 'organizations', orgId, 'members', user.uid));
    if (!snap.exists()) return null;
    const d: any = snap.data();
    const role: OrgRole = (['owner', 'admin', 'member', 'billing', 'viewer'] as const).includes(d?.role)
      ? d.role : 'viewer';
    const perms = d?.permissions || {};
    return {
      role,
      permissions: {
        edit_org_info: perms.edit_org_info === true,
        manage_billing: perms.manage_billing === true,
        invite: perms.invite === true,
      },
    };
  } catch (e) {
    console.warn('[organizations] membership load failed', e);
    return null;
  }
}

/** Every org the user belongs to (back-pointer driven). */
export async function listMyOrganizations(): Promise<Organization[]> {
  try {
    const user = getAuth().currentUser;
    if (!user) return [];
    const snap = await getDoc(doc(getFirestore(), 'users', user.uid));
    const ids: string[] = Array.isArray((snap.data() as any)?.organizations)
      ? (snap.data() as any).organizations : [];
    const orgs = await Promise.all(ids.map((id) => loadOrganization(id)));
    return orgs.filter((o): o is Organization => o !== null);
  } catch {
    return [];
  }
}

/**
 * Save org fields. The users/{uid} ROOT projection (firm_name /
 * organization_employee_count) is mirrored for the org OWNER so the plan
 * recommender and legacy surfaces stay correct — the org doc is canonical.
 */
export async function saveOrganization(
  orgId: string,
  fields: Partial<Pick<Organization, (typeof ORG_EDITABLE_FIELDS)[number]>>,
): Promise<boolean> {
  try {
    const user = getAuth().currentUser;
    if (!user) return false;
    const db = getFirestore();
    const clean: Record<string, string> = {};
    for (const k of ORG_EDITABLE_FIELDS) {
      const v = (fields as any)[k];
      if (typeof v === 'string') clean[k] = v.trim();
    }
    if (Object.keys(clean).length === 0) return true;
    await setDoc(doc(db, 'organizations', orgId), {
      ...clean,
      updated_at: serverTimestamp(),
    }, { merge: true });
    // Projection mirror (self-founded org only — orgId encodes the owner).
    if (orgId === selfFoundedOrgId(user.uid)) {
      const mirror: Record<string, string> = {};
      if (clean.name !== undefined) mirror.firm_name = clean.name;
      if (clean.size_band !== undefined) mirror.organization_employee_count = clean.size_band;
      if (Object.keys(mirror).length > 0) {
        await setDoc(doc(db, 'users', user.uid), mirror, { merge: true });
      }
    }
    return true;
  } catch (e) {
    console.warn('[organizations] save failed', e);
    return false;
  }
}

// ── Phase 2: member management ──────────────────────────────────────────────

export interface OrgMemberRecord extends OrgMembership {
  uid: string;
  display_name: string;
  isOwner: boolean;
}

/** Every member of an org, owner first then by name. Empty on any failure. */
export async function listMembers(orgId: string): Promise<OrgMemberRecord[]> {
  try {
    const db = getFirestore();
    const orgSnap = await getDoc(doc(db, 'organizations', orgId));
    const ownerUid: string = (orgSnap.data() as any)?.owner_uid || '';
    const snap = await getDocs(collection(db, 'organizations', orgId, 'members'));
    const out: OrgMemberRecord[] = snap.docs.map((m) => {
      const d: any = m.data();
      const role: OrgRole = (['owner', 'admin', 'member', 'billing', 'viewer'] as const).includes(d?.role)
        ? d.role : 'viewer';
      const perms = d?.permissions || {};
      return {
        uid: m.id,
        display_name: typeof d?.display_name === 'string' ? d.display_name : '',
        isOwner: m.id === ownerUid,
        role,
        permissions: {
          edit_org_info: perms.edit_org_info === true,
          manage_billing: perms.manage_billing === true,
          invite: perms.invite === true,
        },
      };
    });
    return out.sort((a, b) =>
      a.isOwner !== b.isOwner ? (a.isOwner ? -1 : 1) : a.display_name.localeCompare(b.display_name));
  } catch (e) {
    console.warn('[organizations] listMembers failed', e);
    return [];
  }
}

/**
 * Change a member's role (permissions reset to the role template) or toggle a
 * single permission. Guards mirror the Firestore rules — the rules are the
 * enforcement; these keep the UI honest:
 *   never the owner, never yourself, never granting 'owner'.
 */
export async function updateMember(
  orgId: string,
  memberUid: string,
  change: { role?: Exclude<OrgRole, 'owner'> } | { permission: keyof OrgPermissions; value: boolean },
): Promise<boolean> {
  try {
    const user = getAuth().currentUser;
    if (!user || memberUid === user.uid) return false;
    const db = getFirestore();
    const ref = doc(db, 'organizations', orgId, 'members', memberUid);
    if ('role' in change && change.role) {
      await updateDoc(ref, {
        role: change.role,
        permissions: defaultPermissionsForRole(change.role),
        updated_at: serverTimestamp(),
      });
    } else if ('permission' in change) {
      await updateDoc(ref, {
        [`permissions.${change.permission}`]: change.value,
        updated_at: serverTimestamp(),
      });
    }
    return true;
  } catch (e) {
    console.warn('[organizations] updateMember failed', e);
    return false;
  }
}

/** Remove a member (never the owner, never yourself — rules also enforce). */
export async function removeMember(orgId: string, memberUid: string): Promise<boolean> {
  try {
    const user = getAuth().currentUser;
    if (!user || memberUid === user.uid) return false;
    await deleteDoc(doc(getFirestore(), 'organizations', orgId, 'members', memberUid));
    return true;
  } catch (e) {
    console.warn('[organizations] removeMember failed', e);
    return false;
  }
}

// ── Phase 3: primary organization ───────────────────────────────────────────

/**
 * Choose which organization drives workspace defaults (branding, welcome,
 * plan recommendations). Membership-validated: you can only make an org
 * primary if you belong to it.
 */
export async function setPrimaryOrg(orgId: string): Promise<boolean> {
  try {
    const user = getAuth().currentUser;
    if (!user) return false;
    const db = getFirestore();
    const member = await getDoc(doc(db, 'organizations', orgId, 'members', user.uid));
    if (!member.exists()) return false;
    await setDoc(doc(db, 'users', user.uid), { primary_org: orgId }, { merge: true });
    return true;
  } catch (e) {
    console.warn('[organizations] setPrimaryOrg failed', e);
    return false;
  }
}
